"use client";

import {
  FormControl,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import {
  type Control,
  Controller,
  type FieldErrors,
  type Path,
  type UseFormRegister,
} from "react-hook-form";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useRef, useEffect, useState } from "react";
import { TimePicker, timeStringToValue, timeValueToString, type TimeValue } from "./TimePicker";

export interface TimeInputProps<T extends Record<string, any>> {
  name: Path<T>;
  label: string;
  errors?: FieldErrors<T>;
  register?: UseFormRegister<T>;
  control?: Control<T>;
  textColor?: string;
  placeholder?: string;
  required?: boolean;
  /** Hour, minute, and AM/PM selects in one row on all breakpoints */
  inlineRow?: boolean;
}

export const TimeInput = <T extends Record<string, any>>({
  name,
  label,
  errors,
  register,
  control,
  textColor: textColorProp,
  required = false,
  inlineRow = false,
}: TimeInputProps<T>) => {
  const theme = useThemeTokens();
  const textColor = textColorProp || theme.text.primary;
  const error = errors?.[name];
  const isInvalid = !!error;
  
  const errorMessage = error && typeof error === 'object' && 'message' in error 
    ? (error.message as string | undefined)
    : undefined;

  if (control) {
    return (
      <FormControl isInvalid={isInvalid} mb={6}>
        <FormLabel
          ms="4px"
          mb="5px"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          letterSpacing="-0.01em"
          display="block"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const [currentTimeValue, setCurrentTimeValue] = useState<TimeValue>(() => 
              timeStringToValue(field.value)
            );
            const isInternalUpdate = useRef(false);
            const lastSyncedValue = useRef<string | undefined>(field.value);
            
            useEffect(() => {
              if (isInternalUpdate.current) {
                isInternalUpdate.current = false;
                lastSyncedValue.current = field.value;
                return;
              }
              
              if (field.value !== lastSyncedValue.current) {
                const newTimeValue = timeStringToValue(field.value);
                setCurrentTimeValue(newTimeValue);
                lastSyncedValue.current = field.value;
              }
            }, [field.value]);

            const handleChange = (newValue: TimeValue) => {
              setCurrentTimeValue(newValue);
              
              if (newValue.hour && newValue.minute && newValue.period) {
                const time24 = timeValueToString(newValue);
                isInternalUpdate.current = true;
                field.onChange(time24);
                field.onBlur();
              }
            };

            return (
              <TimePicker
                value={currentTimeValue}
                onChange={handleChange}
                error={errorMessage ? { message: errorMessage } : undefined}
                required={required}
                showErrorMessage={false}
                inlineRow={inlineRow}
              />
            );
          }}
        />
        {errorMessage && (
          <Text color={theme.accent.primary} fontSize="xs" fontWeight="500" mt={1.5}>
            {errorMessage}
          </Text>
        )}
      </FormControl>
    );
  }

  if (register) {
    const registerProps = register(name);
    const [timeValue, setTimeValue] = useState<TimeValue>({ hour: "", minute: "", period: "" });

    useEffect(() => {
      const formInput = document.querySelector(`input[name="${name}"]`) as HTMLInputElement;
      if (formInput?.value) {
        setTimeValue(timeStringToValue(formInput.value));
      }
    }, [name]);

    const handleChange = (newValue: TimeValue) => {
      setTimeValue(newValue);
      const time24 = timeValueToString(newValue);

      const hiddenInput = document.querySelector(`input[name="${name}"][type="hidden"]`) as HTMLInputElement;
      if (hiddenInput) {
        hiddenInput.value = time24;
        const event = new Event("input", { bubbles: true });
        hiddenInput.dispatchEvent(event);
      }
    };

    return (
      <FormControl isInvalid={isInvalid} mb={6}>
        <FormLabel
          ms="4px"
          mb="5px"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          letterSpacing="-0.01em"
          display="block"
        >
          {label}
          {required && (
            <Text as="span" color={theme.accent.primary} ml={1}>
              *
            </Text>
          )}
        </FormLabel>
        <input {...registerProps} type="hidden" name={name} />
        <TimePicker
          value={timeValue}
          onChange={handleChange}
          error={errorMessage ? { message: errorMessage } : undefined}
          required={required}
          showErrorMessage={false}
          inlineRow={inlineRow}
        />
        {errorMessage && (
          <Text color={theme.accent.primary} fontSize="xs" fontWeight="500" mt={1.5}>
            {errorMessage}
          </Text>
        )}
      </FormControl>
    );
  }

  return null;
};
